import { ref, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router/composables'
import CONFIG from '~config/env'
import RestApi from '~services/restApi'
import { v4 as uuid } from 'uuid'

interface DownloadFile {
  url?: string
  name?: string
  key?: string
  customName?: string
  signatureToken?: string
  urlSigned?: string
  type?: string
  id?: string
  path?: string
  documentType?: {
    slug: string
  }
  pages?: Array<{
    deleted: boolean
    type: string
    page: number
  }>
  residentDocuments?: {
    nodes: Array<{
      resident?: {
        legalResponsible?: boolean
      }
    }>
  }
}

export function useDownload() {
  const instance = getCurrentInstance()
  const toasted = instance?.proxy?.$toasted
  const store = instance?.proxy?.$store
  const router = useRouter()

  const MIME_TYPES = {
    PDF: 'application/pdf',
    TXT: 'text/plain',
    CSV: 'text/csv',
    XML: 'text/xml'
  }

  const download = (mimeType: string, filename: string, text?: string, customName?: string) => {
    const linkElement = document.createElement('a')
    linkElement.style.display = 'none'
    linkElement.setAttribute('target', '_blank')

    if (text && (mimeType === MIME_TYPES.TXT || mimeType === MIME_TYPES.CSV || mimeType === MIME_TYPES.XML)) {
      linkElement.href = 'data:' + mimeType + ';charset=utf-8,' + encodeURIComponent(text)
      linkElement.download = filename
    } else {
      linkElement.href = filename
      const name = filename.substring(filename.lastIndexOf('/') + 1, filename.length)
      linkElement.download = customName || name
    }

    document.body.appendChild(linkElement)
    linkElement.click()
    document.body.removeChild(linkElement)
  }

  const getSignedS3Url = async (file: DownloadFile, customName?: string) => {
    if (!file?.url) {
      toasted.error('URL Inválida')
      return
    }

    const key = file.url.replace(CONFIG.AWS.S3_PUBLIC_URL + '/', '').replace(CONFIG.AWS.S3_PRIVATE_URL + '/', '')
    const signedUrl = await RestApi.getAwsSignedUrl(key, customName || file.name)
    return signedUrl.data
  }

  const getMultipleSignedS3Url = async (files: DownloadFile[]) => {
    if (!files?.length) {
      toasted.error('Nenhum arquivo selecionado')
      return
    }

    const filesKeys = files.map(file => file.key).join(',')
    const filesCustomNames = files.map(file => file.customName).join(',')

    const signedUrl = await RestApi.getAwsMultipleSignedUrl(filesKeys, filesCustomNames)
    return signedUrl.data
  }

  const getSignedZapSignUrl = async (file: DownloadFile, useSignedFile: boolean) => {
    if (!file?.url) {
      toasted.error('URL Inválida')
      return
    }

    if (!file?.signatureToken) {
      toasted.error('Token de assinatura inválido')
      return
    }

    const token = file.signatureToken
    const { data: signedUrls } = await RestApi.getSignedDocumentsUrl(token)

    if (!signedUrls.signed_file && !signedUrls.original_file) {
      toasted.error('Erro ao encontrar o arquivo no servidor')
      return
    }

    if (useSignedFile) {
      return signedUrls.signed_file
    } else if (file.url) {
      return signedUrls.original_file
    }
  }

  const downloadFileFromS3 = async (file: DownloadFile, customName?: string) => {
    const formattedName = String(customName || file.name).replace(/ /g, '+')
    const url = await getSignedS3Url(file, formattedName)
    download('', url, undefined, formattedName)
  }

  const downloadFileFromZapSign = async (file: DownloadFile, useSignedFile: boolean, customName?: string) => {
    const url = await getSignedZapSignUrl(file, useSignedFile)
    download('', url, undefined, customName || file.name)
  }

  const downloadByPhotoPath = async (path: string) => {
    const file = { url: path, name: 'photo' }
    await downloadFileFromS3(file)
  }

  const getFullUrl = (url: string) => {
    const baseReportURL = 'https://app.regularizei.app.br'
    const authToken = store.getters.authToken
    return baseReportURL + url + '?token=' + authToken
  }

  const getJobToGenerateLandMainDocument = async (land: any, landDocuments: any[], jobBatchId: string) => {
    const documentTypes = store.state.documentsModule.documentTypes
    const selectedProject = store.state.projectsModule.selectedProject
    const authToken = store.getters.authToken
    const loggedUser = store.getters.loggedUser

    const docTypeLandDossie = documentTypes.find(({ slug }: any) => slug === 'land-main-document')

    if (!docTypeLandDossie) {
      toasted.error('Não foi possível encontrar o tipo de documento "Dossiê do Lote"')
      return
    }

    const documentTypeSlugOrder = [
      { slug: 'zapsign-uniao-estavel', pages: [0, 1, -2, -1] },
      { slug: 'zapsign-casados', pages: [0, 1, -2, -1] },
      { slug: 'zapsign-outros', pages: [0, 1, 7] },
      { slug: 'procuracao' },
      { slug: 'declaracao-insuficiencia-recursos' },
      { slug: 'rg', legalResponsible: true },
      { slug: 'cpf', legalResponsible: true },
      { slug: 'rg', legalResponsible: false },
      { slug: 'cpf', legalResponsible: false },
      { slug: 'certidao-nascimento', legalResponsible: true },
      { slug: 'certidao-obito', legalResponsible: true },
      { slug: 'certidao-casamento', legalResponsible: true },
      { slug: 'certidao-casamento-divorcio', legalResponsible: true },
      { slug: 'certidao-uniao-estavel', legalResponsible: true },
      { slug: 'declaracao-separacao', legalResponsible: true },
      { slug: 'comprovante-tempo-posse' },
      { slug: 'comprovante-endereco' }
    ]

    const files = []

    const documents = landDocuments
      .filter(document => {
        const documentResident = document.residentDocuments.nodes[0]?.resident || {}
        let slugs = documentTypeSlugOrder.filter(item => item.slug === document.documentType.slug)
        if (slugs.some(slug => slug.legalResponsible !== undefined)) {
          slugs = slugs.filter(item => documentResident.legalResponsible === item.legalResponsible)
        }
        return slugs.length > 0
      })
      .sort((a, b) => {
        const aLegalResponsible = a.residentDocuments.nodes[0]?.resident?.legalResponsible
        const bLegalResponsible = b.residentDocuments.nodes[0]?.resident?.legalResponsible
        if (aLegalResponsible === bLegalResponsible) {
          return 0
        }
        return aLegalResponsible ? -1 : 1
      })
      .sort((a, b) => {
        const aIndex = documentTypeSlugOrder.findIndex(({ slug }) => slug === a.documentType.slug)
        const bIndex = documentTypeSlugOrder.findIndex(({ slug }) => slug === b.documentType.slug)
        return aIndex - bIndex
      })

    for (const document of documents) {
      const pages = document.pages?.filter((page: any) => !page.deleted) || []

      if (String(document.url || '').indexOf('zapsign') !== -1) {
        const url = await getSignedZapSignUrl(document, !!document.urlSigned)
        const slug = documentTypeSlugOrder.find(({ slug }) => slug === document.documentType.slug)
        files.push({ url, type: 'pdf', pages: slug?.pages })
      } else {
        const hasImagePages = pages.some((page: any) => page.type.startsWith('image/'))
        const nonImagePages = pages.filter((page: any) => !page.type.startsWith('image/'))

        if (hasImagePages) {
          const documentReport = router.resolve({
            name: 'document-print',
            params: {
              projectId: selectedProject.id,
              documentId: document.id
            }
          })

          const url = getFullUrl(documentReport.href)
          files.push({ url })
        } else if (nonImagePages.length) {
          for (const page of nonImagePages) {
            const url = await getSignedS3Url(page, `${document.name} - Página ${page.page}.pdf`)
            files.push({ url, type: 'pdf' })
          }
        }
      }
    }

    if (files.length) {
      const landSketch = router.resolve({
        name: 'land-sketch',
        params: {
          projectId: selectedProject.id,
          landId: land.id
        }
      })
      const landSketchUrl = getFullUrl(landSketch.href)
      files.push({ url: landSketchUrl })

      const landMemorial = router.resolve({
        name: 'land-memorial',
        params: {
          projectId: selectedProject.id,
          landId: land.id
        }
      })
      const landMemorialhUrl = getFullUrl(landMemorial.href)
      files.push({ url: landMemorialhUrl })

      return {
        job: {
          jobBatchId,
          referenceId: land.id,
          type: 'PDF_GENERATION',
          status: 'CREATED',
          payload: {
            token: authToken,
            projectId: selectedProject.id,
            landId: land.id,
            docTypeId: docTypeLandDossie.id,
            uuid: uuid(),
            files
          }
        }
      }
    }

    return null
  }

  return {
    MIME_TYPES,
    download,
    getSignedS3Url,
    getMultipleSignedS3Url,
    getSignedZapSignUrl,
    downloadFileFromS3,
    downloadFileFromZapSign,
    downloadByPhotoPath,
    getFullUrl,
    getJobToGenerateLandMainDocument
  }
}
